import * as React from 'react';

import { cn } from '@/lib/utils/utils';

import type {
  ToggleButtonProps,
  ToggleButtonOptionProps,
} from './toggle-button-types';

/**
 * ToggleButton Component
 *
 * A segmented control component that allows users to switch between multiple options.
 * Based on the Figma MCP design with "Explain" and "Guide" options.
 *
 * Features:
 * - Two-option toggle with active/inactive states
 * - Smooth visual transitions
 * - Keyboard navigation support
 * - Accessible with proper ARIA attributes
 * - Matches Figma design specifications exactly
 */
const ToggleButton = React.forwardRef<HTMLDivElement, ToggleButtonProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue,
      onChange,
      disabled = false,
      className,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(() => {
      // If defaultValue is provided and is valid, use it
      if (
        defaultValue &&
        options.some(option => option.value === defaultValue)
      ) {
        return defaultValue;
      }
      // Otherwise, fall back to first option
      return options[0]?.value || '';
    });

    const isControlled = controlledValue !== undefined;
    const selectedValue = isControlled ? controlledValue : internalValue;

    const handleOptionClick = React.useCallback(
      (optionValue: string) => {
        if (!isControlled) {
          setInternalValue(optionValue);
        }
        onChange?.(optionValue);
      },
      [isControlled, onChange]
    );

    // Update internal value when controlled value changes
    React.useEffect(() => {
      if (isControlled) {
        setInternalValue(controlledValue || '');
      }
    }, [controlledValue, isControlled]);

    const containerSizeClasses = {
      sm: 'h-8',
      md: 'h-10',
      lg: 'h-12',
    };

    return (
      <div
        ref={ref}
        className={cn(
          // Base container styles matching Figma design
          'relative inline-flex items-center rounded-2xl bg-magical-moonlight-d2 p-0',
          containerSizeClasses[size],
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        role="radiogroup"
        aria-disabled={disabled}
        {...props}
      >
        {options.map(option => (
          <ToggleButtonOption
            key={option.value}
            option={option}
            isSelected={selectedValue === option.value}
            isDisabled={disabled}
            onClick={handleOptionClick}
            size={size}
          />
        ))}
      </div>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';

/**
 * Individual Toggle Button Option Component
 */
const ToggleButtonOption: React.FC<ToggleButtonOptionProps> = ({
  option,
  isSelected,
  isDisabled,
  onClick,
  size,
}) => {
  const handleClick = () => {
    if (!isDisabled) {
      onClick(option.value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const sizeClasses = {
    sm: 'px-3 py-1 text-sm h-8',
    md: 'px-5 py-2 text-base h-10',
    lg: 'px-6 py-3 text-lg h-12',
  };

  return (
    <button
      type="button"
      className={cn(
        // Base button styles
        'relative flex items-center justify-center font-medium transition-all duration-200 ease-in-out leading-[24px]',
        'text-white',
        'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-magical-moonlight-d2',
        sizeClasses[size],

        // First option (left) styling
        option.value === 'explain' && [
          'rounded-2xl min-w-[99px]',
          isSelected
            ? 'bg-bhanzu-orange text-white shadow-md' // Active state - orange background
            : 'bg-transparent text-white hover:bg-bhanzu-orange/20', // Inactive state - transparent with hover
        ],

        // Second option (right) styling
        option.value === 'guide' && [
          'rounded-2xl min-w-[85px]',
          isSelected
            ? 'bg-bhanzu-orange text-white shadow-md' // Active state - orange background
            : 'bg-transparent text-white hover:bg-bhanzu-orange/20', // Inactive state - transparent with hover
        ],

        // Disabled state
        isDisabled && 'cursor-not-allowed opacity-50',

        // Interactive states
        !isDisabled && !isSelected && 'hover:bg-bhanzu-orange/10'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      tabIndex={isSelected ? 0 : -1}
    >
      <span className="relative z-10 select-none whitespace-nowrap">
        {option.label}
      </span>
    </button>
  );
};

export { ToggleButton };
export type {
  ToggleButtonProps,
  ToggleButtonOption,
} from './toggle-button-types';
